/**
 * Notification Service - Handles all notification methods
 */

import { NotificationMethod } from '../types/reminder';
import { Reminder } from '../types/reminder';
import { downloadICSFile } from '../utils/icsGenerator';
import { getFunctions, httpsCallable, HttpsCallableResult } from 'firebase/functions';
import app, { auth } from '../config/firebase';

// ============================================================================
// Common Response Types
// ============================================================================

export interface NotificationResponse {
  success: boolean;
  messageId?: string;
  error?: string;
}

// ============================================================================
// Reminder Email Types
// ============================================================================

export interface SendReminderEmailRequest {
  to: string;
  reminderTitle: string;
  reminderDescription?: string;
  reminderDate: string;
  reminderTime: string;
  reminderPriority: string;
  reminderLocation?: string;
  linkedEntity?: string;
  portalUrl?: string;
}

export class NotificationService {
  /**
   * Request permission for browser notifications
   */
  static async requestNotificationPermission(): Promise<boolean> {
    if (!('Notification' in window)) {
      console.warn('This browser does not support notifications');
      return false;
    }

    if (Notification.permission === 'granted') {
      return true;
    }

    if (Notification.permission !== 'denied') {
      const permission = await Notification.requestPermission();
      return permission === 'granted';
    }

    return false;
  }

  /**
   * Show browser push notification
   */
  static async showPushNotification(title: string, body: string, icon?: string): Promise<void> {
    const hasPermission = await this.requestNotificationPermission();

    if (!hasPermission) {
      console.warn('Notification permission not granted');
      return;
    }

    // Try to use service worker notification if available
    if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
      try {
        const registration = await navigator.serviceWorker.ready;
        await registration.showNotification(title, {
          body,
          icon: icon || '/icons/icon-192x192.png',
          badge: '/icons/icon-192x192.png',
          tag: 'lucid-dock-reminder',
          requireInteraction: true,
        });
        return;
      } catch (error) {
        console.warn('Service worker notification failed, falling back to regular notification', error);
      }
    }

    // Fallback to regular notification
    new Notification(title, {
      body,
      icon: icon || '/icons/icon-192x192.png',
      requireInteraction: true,
    });
  }

  /**
   * Show in-app banner notification
   */
  static showBannerNotification(title: string, description: string, onDismiss?: () => void): void {
    const event = new CustomEvent('lucid-dock-banner', {
      detail: { title, description, onDismiss }
    });
    window.dispatchEvent(event);
  }

  /**
   * Open email client with pre-filled reminder (mailto: link)
   */
  static sendEmailNotification(title: string, details: string, recipientEmail?: string): void {
    const subject = encodeURIComponent(`Reminder: ${title}`);
    const body = encodeURIComponent(details);
    const to = recipientEmail || '';

    const mailtoLink = `mailto:${to}?subject=${subject}&body=${body}`;
    window.open(mailtoLink, '_blank');
  }

  /**
   * Download .ics calendar file
   */
  static downloadCalendarFile(reminder: Reminder): void {
    try {
      downloadICSFile(reminder);
    } catch (error) {
      console.error('Failed to download calendar file:', error);
    }
  }

  /**
   * Trigger a notification using the specified method
   */
  static async triggerNotification(
    method: NotificationMethod,
    title: string,
    description: string,
    _addressLabel?: string,
    reminder?: Reminder
  ): Promise<void> {
    const body = description;

    switch (method) {
      case 'push':
        await this.showPushNotification(title, body);
        break;

      case 'banner':
        this.showBannerNotification(title, body);
        if (reminder) {
          this.showReminderAlert(reminder);
        }
        break;

      case 'alert':
        if (reminder) {
          this.showReminderAlert(reminder);
        }
        break;

      case 'notification':
        if (reminder) {
          this.addToNotificationPanel(reminder);
        }
        break;

      case 'email':
        if (reminder) {
          // Get user email from Firebase Auth (use imported auth instance)
          const userEmail = auth.currentUser?.email;
          if (userEmail) {
            // Build linked entity label
            let linkedEntity: string | undefined;
            if (reminder.addressLabel) linkedEntity = reminder.addressLabel;
            else if (reminder.vehicleLabel) linkedEntity = reminder.vehicleLabel;
            else if (reminder.healthLabel) linkedEntity = reminder.healthLabel;
            else if (reminder.financialLabel) linkedEntity = reminder.financialLabel;
            else if (reminder.insuranceLabel) linkedEntity = reminder.insuranceLabel;

            try {
              const result = await this.sendReminderEmail({
                to: userEmail,
                reminderTitle: reminder.title,
                reminderDescription: reminder.description,
                reminderDate: reminder.date,
                reminderTime: reminder.time,
                reminderPriority: reminder.priority,
                reminderLocation: reminder.location,
                linkedEntity,
                portalUrl: reminder.portalUrl,
              });
              if (!result.success) {
                console.error('[NotificationService] Failed to send reminder email:', result.error);
              }
            } catch (error) {
              console.error('[NotificationService] Error sending reminder email:', error);
            }
          } else {
            // User not authenticated or email not available
            console.warn('[NotificationService] Cannot send email: User not authenticated or email not available');
            // Fallback to mailto if no user email
            const emailDetails = `${title}\n\n${body}\n\nSent from LucidDock`;
            this.sendEmailNotification(title, emailDetails);
          }
        } else {
          // Fallback to mailto for non-reminder emails
          const emailDetails = `${title}\n\n${body}\n\nSent from LucidDock`;
          this.sendEmailNotification(title, emailDetails);
        }
        break;

      case 'calendar-download':
        if (reminder) {
          this.downloadCalendarFile(reminder);
        } else {
          console.warn('Cannot download calendar file: reminder data not provided');
        }
        break;

      case 'sms':
        console.warn('SMS notifications are not supported.');
        break;

      case 'call':
      case 'whatsapp':
      case 'voice-assistant':
        console.warn(`${method} notification not yet implemented`);
        break;
    }
  }

  /**
   * Show in-app reminder alert modal
   */
  static showReminderAlert(reminder: Reminder): void {
    const event = new CustomEvent('reminder-alert', {
      detail: { reminder }
    });
    window.dispatchEvent(event);
  }

  /**
   * Add reminder to notification panel
   */
  static addToNotificationPanel(reminder: Reminder): void {
    const event = new CustomEvent('add-to-notification-panel', {
      detail: { reminder }
    });
    window.dispatchEvent(event);
  }

  /**
   * Check notification support
   */
  static isNotificationSupported(method: NotificationMethod): boolean {
    switch (method) {
      case 'push':
        return 'Notification' in window;
      case 'banner':
        return true;
      case 'alert':
        return true;
      case 'notification':
        return true;
      case 'email':
        return true;
      case 'calendar-download':
        return true;
      default:
        return false;
    }
  }

  /**
   * Get notification status
   */
  static getNotificationStatus(): {
    supported: boolean;
    permission: NotificationPermission | 'unsupported';
  } {
    if (!('Notification' in window)) {
      return { supported: false, permission: 'unsupported' };
    }

    return {
      supported: true,
      permission: Notification.permission
    };
  }

  // ============================================================================
  // Firebase Cloud Functions
  // ============================================================================

  private static functions = getFunctions(app);

  // ============================================================================
  // Reminder Email Functions
  // ============================================================================

  /**
   * Send Reminder Email Notification via Cloud Function
   * Sends a beautifully formatted email notification for reminders
   */
  static async sendReminderEmail(request: SendReminderEmailRequest): Promise<NotificationResponse> {
    try {
      const fn = httpsCallable<SendReminderEmailRequest, NotificationResponse>(
        this.functions,
        'sendReminderEmail'
      );
      const result: HttpsCallableResult<NotificationResponse> = await fn(request);
      return result.data;
    } catch (error) {
      console.error('Failed to send reminder email:', error);
      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to send email. Please try again.',
      };
    }
  }
}
